import React, { useState } from "react";
import { Row, Col, Tabs, Alert, Typography } from "antd";

import { W0, W1 } from "@components/Constants";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { type OrganizationPublicKey } from "@api/types";

import KeyUser from "./KeyUser";
import CodeBlock from "./CodeBlock";
import ClaimsList from "./ClaimsList";
import OrganizationPublicKeyAuthenticationsTable from "./OrganizationPublicKeyAuthenticationsTable";
import getClaims from "./helpers/getClaims";
import getIdTokenSnippet from "./helpers/getIdTokenSnippet";
import { AUDIENCE_BACKSTAGE } from "./helpers/audiences";

const LABEL_KEY_ID = "Key ID (kid header)";
const LABEL_ISSUER = "Issuer (iss claim)";
const LABEL_CLAIMS = "Claims";
const LABEL_PUBLIC_KEY = "Public key";
const LABEL_SNIPPET = "Issue an ID token";
const LABEL_AUTHENTICATIONS = "Authentications";

const DESCRIPTION_COMMON = "Your client signs each ID token with the private key of this public key. The algorithm must be RS256, and each claim below is necessary.";

const DESCRIPTION_BACKSTAGE = "The token acts as the user above and carries the access of that person. An operator that removes the user, or that disables the user, stops this key.";

const DESCRIPTION_INVESTMENTS = "This key signs in your own investors. Make the investor account of the address first, because the platform finds that account and it makes no account of its own. The platform binds each account to your organization at the first token of that account.";

const LABEL_RESTRICTION = "The platform refuses an address that it already holds";

const DESCRIPTION_RESTRICTION = "The investor must sign in to your portal only. The platform refuses the token when the address is the address of a user of the back office of an organization, when the investor holds an account of the Fundamental investor portal, or when a different organization signs in that address. Each investor of this key must be external to the Fundamental portals.";

const COLUMN_SPAN = 24;

// NOTE: the snippet is the tallest block of the modal. The value keeps the
//       block inside the window, and it counts the title, the two values
//       above the tabs and the tab bar.
const SNIPPET_MAX_HEIGHT = "calc(100vh - 340px)";

const TAB_CLAIMS = "claims";
const TAB_SNIPPET = "snippet";
const TAB_PUBLIC_KEY = "publicKey";
const TAB_AUTHENTICATIONS = "authentications";

type Props = {
  publicKey: OrganizationPublicKey;
};

const OrganizationPublicKeyDetails = ({ publicKey }: Props): JSX.Element => {
  const { identity } = useAppContext() as AppContext;

  const [ activeTabKey, setActiveTabKey ] = useState(TAB_CLAIMS);

  const {
    id,
    issuer,
    audience,
    backstageUserId,
    publicKey: publicKeyPem,
  } = publicKey;

  const isBackstageAudience = audience === AUDIENCE_BACKSTAGE;

  const description = isBackstageAudience
    ? DESCRIPTION_BACKSTAGE
    : DESCRIPTION_INVESTMENTS;

  const claims = getClaims(audience);

  const snippet = getIdTokenSnippet({
    domain: identity?.organization?.domain,
    issuer,
    audience,
    publicKeyId: id,
  });

  const labelStyle = {
    marginBottom: W0 / 2,
  };

  const valueStyle = {
    marginBottom: W1,
    wordBreak: "break-all" as const,
  };

  const renderValue = (label: string, value: string) => (
    <Col span={COLUMN_SPAN}>
      <Typography.Paragraph type="secondary" style={labelStyle}>
        {label}
      </Typography.Paragraph>

      <Typography.Paragraph
        code
        copyable={{ text: value }}
        style={valueStyle}
      >
        {value}
      </Typography.Paragraph>
    </Col>
  );

  // NOTE: the Claims tab holds text only, therefore it gives no code and the
  //       copy control below is not there for it.
  const codeByTabKey: Record<string, string | undefined> = {
    [TAB_SNIPPET]: snippet,
    [TAB_PUBLIC_KEY]: publicKeyPem,
  };

  const activeCode = codeByTabKey[activeTabKey];

  const hasActiveCode = !!activeCode;

  const hasKeyUser = !!backstageUserId;

  // NOTE: the table reads the trail when the operator opens the tab, and
  //       not when the details of the key open.
  const isAuthenticationsTab = activeTabKey === TAB_AUTHENTICATIONS;

  const claimsTabChildren = (
    <>
      <Typography.Paragraph type="secondary">
        {DESCRIPTION_COMMON}

        {" "}

        {description}
      </Typography.Paragraph>

      <Row gutter={W1}>
        {renderValue(LABEL_KEY_ID, id)}
        {renderValue(LABEL_ISSUER, issuer)}
      </Row>

      <ClaimsList claims={claims} />
    </>
  );

  const tabItems = [
    {
      key: TAB_CLAIMS,
      label: LABEL_CLAIMS,
      children: claimsTabChildren,
    },
    {
      key: TAB_SNIPPET,
      label: LABEL_SNIPPET,
      children: (
        <CodeBlock
          code={snippet}
          maxHeight={SNIPPET_MAX_HEIGHT}
        />
      ),
    },
    {
      key: TAB_PUBLIC_KEY,
      label: LABEL_PUBLIC_KEY,
      children: <CodeBlock code={publicKeyPem} />,
    },
    {
      key: TAB_AUTHENTICATIONS,
      label: LABEL_AUTHENTICATIONS,
      children: isAuthenticationsTab
        ? (
          <OrganizationPublicKeyAuthenticationsTable
            keyId={id}
          />
        )
        : null,
    },
  ];

  // NOTE: the copy control keeps the copied state of the tab that it was on,
  //       therefore the key makes a new control for each tab.
  const tabBarExtraContent = {
    right: hasActiveCode
      ? (
        <Typography.Text
          key={activeTabKey}
          copyable={{ text: activeCode }}
        />
      )
      : null,
  };

  return (
    <>
      {hasKeyUser && (
        <div style={{ marginBottom: W1 }}>
          <KeyUser userId={backstageUserId} />
        </div>
      )}

      {/* NOTE: the client of a key of the back office asserts no address,
                therefore the rules of the address reach that key at no
                point and the note is of the investor API only. */}
      {!isBackstageAudience && (
        <Alert
          showIcon
          type="warning"
          message={LABEL_RESTRICTION}
          description={DESCRIPTION_RESTRICTION}
          style={{ marginBottom: W1 }}
        />
      )}

      <Tabs
        size="small"
        items={tabItems}
        activeKey={activeTabKey}
        tabBarExtraContent={tabBarExtraContent}
        onChange={setActiveTabKey}
      />
    </>
  );
};

export default OrganizationPublicKeyDetails;
